import {
  DataTypeOption,
  TrainingSetDataConfig,
} from '../components/fields/FieldsSettings/FieldsSettingsTypes';

import { IListResponse } from '../api/types';
import { DocumentDataStatusFilters } from './packager-types';

export interface IUser {
  email: string;
  is_staff?: boolean;
  data_pools: string[];
  organizations: string[];
}
export interface IOrg {
  id: string;
  url: string;
  label: string;
  is_inactive?: boolean;
  data_pools: string[];
  created_at: string;
  updated_at: string;
  tokenizer_version?: string;
  default_tokenizer_version: { name: string; inherited: boolean };
}

export type DataPoolUseType = 'TEST' | 'DEMO' | 'PRODUCTION';
export type DataPoolStatusType = 'ACTIVE' | 'INACTIVE';
export type DataPoolPriorityType = 'High' | 'Normal';

export type ValidationDataset = 'extracted' | 'transformed' | 'schematized';

export interface IDataPoolConfig {
  suggest_new_vendors?: boolean;
  validation_datasets?: ValidationDataset[];
  data_relevant_from?: string | null;
}

export interface IClient {
  id: number;
  organization: number;
  name: string;
  abbr: string;
  description: string;
  data_pools: string[];
}

export interface IDataPool {
  id: string;
  url: string;
  label: string;
  organization: string;
  verify_extractions: boolean;
  glynt_tags: string[];
  routerPrefix: string;
  created_at: string;
  updated_at: string;
  tokenizer_version?: string;
  default_tokenizer_version: { name: string; inherited: boolean };
  public?: boolean;
  use?: DataPoolUseType;
  status?: DataPoolStatusType;
  priority: DataPoolPriorityType;
  config?: IDataPoolConfig;
  client?: IClient | null;
  usage_decimal_places?: number;
}

interface IDatapoolRouteConfigNamingFormula {
  lookup_value: string;
  source: string;
}
export interface IDataPoolRouteConfig {
  org_id: string;
  data_pool_id: string;
  route_prefix: {
    prefix: string;
    priority: DataPoolPriorityType;
  };
  data_packaging_config: {
    separator: string;
    naming_formula: IDatapoolRouteConfigNamingFormula[];
    package_format: {
      type: string;
      options: {
        lookup_value: string;
        source: string;
      };
    };
  };
  package_trigger: {
    type: string;
    config: {
      type: string;
      expressions: [string];
    };
  };
}

export interface IValidationConfig {
  opname: string;
  config?: CrossCheckValidatorConfig;
}

export interface CrossCheckValidatorConfig {
  CONFIGNAME: string;
  label?: string;
  include: {
    pattern: string;
    attribute: string;
  }[];
  exclude: {
    pattern: string;
    attribute: string;
  }[];
}

export interface IField {
  created_at: string;
  data_type?: string;
  data_type_config?: DataTypeOption | null;
  validation_config?: IValidationConfig[] | null;
  field_order: number;
  glynt_tags: string[];
  ground_truths: IGroundTruth[];
  id: string;
  label: string;
  message?: string;
  metafield: string;
  status?: unknown;
  tags: string[];
  training_set: string;
  updated_at: string;
  url: string;
}

export interface IDocument {
  id: string;
  url: string;
  file_access_url: string;
  label: string;
  content_md5: string;
  text_md5: string;
  content_type: string;
  file_extension: string;
  pages: string[];
  tokenizeds: string[];
  tags: string[];
  glynt_tags: string[];
  created_at: string;
  updated_at: string;
  index: number;
  marks?: number;
}

export enum EnumDocumentStatus {
  CREATED = 'CREATED',
  EXCLUDED = 'EXCLUDED',
  TRANSMISSION_SUCCESSFUL = 'TRANSMISSION_SUCCESSFUL',
  TRANSMISSION_FAILED = 'TRANSMISSION_FAILED',
}

export type DocumentRelationshipType = 'duplicate' | 'split' | 'translated' | 'reintroduced';

export interface IDocumentRelationshipEntry {
  document_id: string;
  document_data_id: string;
  data_pool_id: string;
  url: string | null;
  detection_method: string; // MD5 | CONTENT | n/a
}

export interface IDocumentRelationshipSet {
  parent: IDocumentRelationshipEntry | null;
  children: IDocumentRelationshipEntry[];
}

/**
 * The ORIGINAL document a row descends from, through split/translated/reintroduced
 * chains at any depth — not one hop. Null when the row IS the origin.
 *
 * Deliberately not `IDocumentRelationshipEntry`: that types `document_id` non-null and
 * requires `detection_method`, neither of which holds here. The origin's Document row
 * can be deleted while its DocumentData survives (document_id null, url null), and a
 * root is never produced by detection so it carries no method.
 *
 * `data_pool_id` is load-bearing, not decoration: a lineage root may legitimately live
 * in ANOTHER pool, and the manifest query is pool-scoped.
 */
export interface IDocumentLineageRoot {
  document_id: string | null;
  document_data_id: string;
  data_pool_id: string;
  url: string | null;
}

// Keyed by relationship type; only present types appear ({} when none).
export type IDocumentRelationships = Partial<
  Record<DocumentRelationshipType, IDocumentRelationshipSet>
>;

export interface IDocumentUnscoped {
  id: string;
  data_pool_id: string;
  data_pool_label: string;
  org_id: string;
  org_label: string;
  file_extension: string;
  label: string;
  created_at: string;
  updated_at: string;
  token_count: number;
  language: string | null;
  format_id: string | null;
  is_supported: boolean;
  content_md5: string;
  text_md5: string;
  duplicate_count: number;
  extractions: IDocumentUnscopedExtraction[];
  pages: string[];
  tags: string[];
  glynt_tags: string[];
  url: string;
  file_access_url: string;
  content_type: string;
  status: EnumDocumentStatus;
  target_training_set: string | null;
  target_training_set_label: string | null;
  vendor: number | null;
  vendor_name: string | null;
  document_data_id: string | null;
  document_data_status: DocumentDataStatusFilters | null;
  exclusion_reason?: string | null;
  escalation_detail?: string | null;
  /**
   * True if the doc is a child/parent in ANY relationship type — duplicate,
   * split, translated, reintroduced. Do NOT use either flag to decide anything
   * type-specific: reading has_children as "already split" is what disabled
   * Split on the canonical document of every duplicate set. Read
   * `relationships.<type>` instead. Kept because the API serves them and a
   * cypress spec asserts has_children.
   */
  has_parents: boolean;
  has_children: boolean;
  relationships: IDocumentRelationships;
  /** Lineage origin; null both when this row IS the origin and when it has no lineage. */
  root_document_data?: IDocumentLineageRoot | null;
}

export interface IDocumentUnscopedExtraction {
  id: string;
  created_at: string;
  updated_at: string;
  training_set: string;
  training_set_label: string;
  extraction_batch: string;
  extraction_batch_label: string;
  status: string;
  data_processing_status: DataProcessingStatus;
  finished: boolean;
  tags: string[];
  glynt_tags: string[];
  verified: boolean;
  verified_at: string | null;
  verify_expired: boolean;
}

// use for tool nav
export interface IDoc {
  id: string;
  label: string;
  index: number;
}

export interface ITrainingSetCreate {
  label: string;
  documents: string[];
}
export interface ITrainingSet {
  id: string;
  url: string;
  label: string;
  description: string;
  created_at: string;
  updated_at: string;
  tokenizer_version: string;
  fields: string[];
  documents: string[];
  training_revisions: string[];
  tags: string[];
  glynt_tags: string[];
  advisories?: string[];
  ground_truths?: string[];
  training_requested?: boolean;
  default_tokenizer_version?: string;
  data_type_config?: TrainingSetDataConfig | null;
  using_global_data_type_config?: boolean;
  vendor_id?: number | null;
  vendor_name?: string | null;
  vendors?: { id: number }[];
  document_count?: number;
  fields_count?: number;
  commodities?: string[];
  meters_per_commodity?: Record<string, number>;
  total_meter_count?: number;
  marked_vendors?: string[];
  marked_tariffs?: string[];
  language_code?: string;
  trained?: boolean;
  certified_at?: string | null;
  certified_by?: string | null;
}

export interface ITokenized {
  id: string;
  url: string;
  document: string;
  status: string;
  finished: boolean;
  tokenizer_version: string;
  created_at: string;
  updated_at: string;
  tokenized: any;
}

export interface IGroundTruth {
  id: string;
  url: string;
  content: string;
  field: string;
  document: string;
  token_data: ITokenData;
  advisories?: string[];
  created_at: string;
  updated_at: string;
}

export interface ITokenData {
  tokens: IToken[];
}

export interface IToken {
  content: string;
  page: number;
  index: number;
  parent?: IToken;
  parent_index?: number;
  id: string;
  bbox: IBoundingBox[];
  field_label?: string;
  children?: number[];
}

interface IBoundingBox {
  x: number;
  y: number;
}

export interface ITrainingRevisionList {
  count: number;
  next?: number | null;
  previous?: number | null;
  results: ITrainingRevision[];
}

export interface ITrainingRevision {
  created_at: string;
  updated_at: string;
  url: string;
  id: string;
  label: string;
  tokenizer_version: string;
  status: string;
  finished: boolean;
  training_set: string;
  document_ids: string[];
  core_version: string;
  fields?: string[];
  ground_truth?: ITrainingRevisionResultGroundTruth[];
  tags?: string[];
  glynt_tags?: string[];
  benchmark?: boolean;
}

interface ITrainingRevisionResultGroundTruth {
  [key: string]: {
    [key: string]: {
      content: string;
      token_data?: ITokenData;
    };
  };
}

interface IExtractionsByTrainingSet {
  [key: string]: string[];
}

export interface IExtractionVerify {
  expired: boolean;
  is_verified: boolean;
  updated_at: string;
}

export interface IExtractionBatch {
  created_at: string;
  updated_at: string;
  url: string;
  id: string;
  label: string;
  training_set: string;
  training_revision: string;
  fields: string[];
  additional_fields: string[];
  documents: string[];
  extractions: IExtraction[];
  extractions_by_training_set: IExtractionsByTrainingSet;
  data_processing_status: DataProcessingStatus;
  status: ExtractionStatus;
  finished: boolean;
  tags: string[];
  glynt_tags: string[];
  benchmark: boolean;
  is_experimental: boolean;
  verified: null | IExtractionVerify;
  // quick view parameters
  first_extraction?: string;
  training_set_id?: string;
  training_set_label?: string;
  training_set_certified_at?: string | null;
  training_set_certified_by?: string | null;
  has_undismissed_validation_errors?: boolean;
}

export interface IExtraction {
  created_at: string;
  updated_at: string;
  url: string;
  id: string;
  extraction_batch: string;
  training_set: string;
  training_revision: string;
  document: string;
  tokenized: string;
  status: ExtractionStatus;
  finished: boolean;
  benchmark: boolean;
  fields: string[];
  additional_fields: string[];
  corrections: string[];
  glynt_tags: string[];
  results?: IExtractionResults;
  index?: number;
  doc?: IDocument;
  data_processing_status: DataProcessingStatus;
  document_data?: string;
  document_data_status?: DocumentDataStatusFilters;
  exclusion_reason?: string | null;
  escalation_detail?: string | null;
}

export interface IExtractionResults {
  [key: string]: {
    data_type_config?: DataTypeOption | null;
    content: string;
    tokens: IToken[];
    data_type?: string;
    tags: string[];
    glynt_tags: string[];
  };
}

export interface ICorrection {
  created_at: string;
  updated_at: string;
  url: string;
  id: string;
  extraction: string;
  field: string;
  correction: {
    content: string;
  };
}

export interface ITrainingSets {
  count: number;
  next?: string;
  previous?: string;
  results: ITrainingSet[];
}

export enum EToastVariant {
  success = 'success',
  warning = 'warning',
  error = 'error',
  info = 'info',
  download = 'download',
}

export interface IRQCallbacks {
  onSuccess?: (data?: any) => void;
  onError?: (error?: any) => void;
  onMutate?: () => void;
  onSettled?: () => void;
}

export interface INamingOptions {
  type: string;
  value?: string;
}

export interface IDataExportsCreateParams {
  report_type: 'document_manifest';
  filters: any;
}

export interface IDataExportsGetByUserResponse extends IListResponse {
  results: IDataExport[];
}

export interface IDataExport {
  id: string;
  report_type: string;
  status: string;
  file_access_url: string;
  filters: {
    [key: string]: string;
  };
  created_at: string;
}

export interface IDataExportsGetByUserParams {
  user: string;
  limit?: number;
  offset?: number;
}

export const Statuses = {
  Pending: 'Pending',
  InProgress: 'In Progress',
  PartialSuccess: 'Partial Success',
  Success: 'Success',
  Failed: 'Failed',
} as const;

export type Status = (typeof Statuses)[keyof typeof Statuses];

export type DataProcessingStatus = Status | null;

export type ExtractionStatus = Status | null;
